import { parseUrlWithSite } from '@seek/melways-router-utils';
import {
  type AlternativeName,
  isMelwaysSiteName,
  type SiteName,
} from '@seek/melways-sites';
import { type ReactNode, useMemo } from 'react';

import { MelwaysContext } from '../context';

export type MelwaysClientProviderProps = {
  site: SiteName;
  staging?: boolean;
  alternative?: AlternativeName;
  children: ReactNode;
  /*
   * The current location path of your application.
   *
   * Provide the path if you want your client side routing to update the Melways language and locale based on the current path.
   *
   * If not provided, the current window location will be used to determine the Melways context.
   */
  path?: string;
};

export const MelwaysClientProvider = ({
  site,
  staging = false,
  alternative,
  children,
  path,
}: MelwaysClientProviderProps) => {
  if (!window) {
    throw new Error(
      'MelwaysClientProvider must be used on the client side only',
    );
  }

  if (!isMelwaysSiteName(site)) {
    throw new Error('MelwaysClientProvider requires a valid site name.');
  }

  const url = path || window.location.href;

  const value = useMemo(() => {
    const melwaysContext = parseUrlWithSite({
      url,
      site,
      staging,
    });

    return {
      ...melwaysContext,
      ...(alternative ? { alternative } : {}),
    };
  }, [site, staging, alternative, url]);

  return (
    <MelwaysContext.Provider value={value}>{children}</MelwaysContext.Provider>
  );
};
